import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../api/Axios";
import { formatDateToMonthYear, formatDateTimeLocal } from "../../utils/dateUtils";
import ConfirmDeleteModal from '../modals/ConfirmDeleteModal';
import EmptyState from '../common/EmptyState';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';

// Компонент для ячейки с ограниченной высотой и плавным исчезновением текста
const TruncatedCell = ({ text, className = "", lines = 3 }) => {
  const lineHeight = 1.25; // rem
  const containerHeight = `${lines * lineHeight}rem`;
  
  // Проверяем, нужно ли обрезать текст
  const needsTruncation = text && text.length > 100; // Примерная длина для 3 строк
  
  return (
    <td className={`border p-2 relative ${className}`}>
      <div className="relative overflow-hidden" style={{ height: containerHeight }}>
        <div 
          className="text-sm leading-5 truncate-text" 
          style={{ 
            WebkitLineClamp: lines,
            lineHeight: `${lineHeight}rem`,
            maxHeight: containerHeight
          }}
          title={needsTruncation ? text : undefined}
        >
          {text || "—"}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
      </div>
    </td>
  );
};

const ArchiveTable = () => {
  const [groupedTasks, setGroupedTasks] = useState({});
  const [developers, setDevelopers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [archivetasks, setArchivetasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState({}); // Track expanded/collapsed months
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, taskId: null, taskTitle: '' });

  const getProjectName = useCallback((projectId) => {
    const project = projects.find((p) => (p.id || p._id) === projectId);
    return project ? project.name : projectId;
  }, [projects]);

  const fetchData = async () => {
    try {
      const [archivetasksResponse, developersResponse, projectsResponse] =
        await Promise.all([
          axiosInstance.get("/getarchivetasks"),
          axiosInstance.get("/devs"),
          axiosInstance.get("/projects"),
        ]);

      setArchivetasks(archivetasksResponse.data);
      setDevelopers(developersResponse.data);
      setProjects(projectsResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const getLocalDate = (date) => {
      if (!date) return null;
      const dateObj = new Date(date);
      return dateObj;
    };

    const groupTasks = () => {
      const groupedByMonth = archivetasks.reduce((acc, task) => {
        const date = task.completedAt || task.createdAt;
        const localDate = getLocalDate(date);
        const monthYear = localDate
          ? localDate.toLocaleDateString('ru-RU', { year: 'numeric', month: 'long' })
          : 'Без даты';
        if (!acc[monthYear]) acc[monthYear] = {};
        if (!acc[monthYear][task.developer])
          acc[monthYear][task.developer] = [];
        acc[monthYear][task.developer].push({
          ...task,
          projectName: getProjectName(task.project),
        });
        return acc;
      }, {});

      // Сортировка задач в каждой группе по дате завершения (самые недавние сверху)
      for (const month in groupedByMonth) {
        for (const developer in groupedByMonth[month]) {
          groupedByMonth[month][developer].sort(
            (a, b) => new Date(b.completedAt) - new Date(a.completedAt) // Обратная сортировка - новые сверху
          );
        }
      }

      setGroupedTasks(groupedByMonth);
    };
    groupTasks();
  }, [archivetasks, getProjectName]);

  const getDeveloperInfo = (developerId) => {
    const developer = developers.find((d) => (d.id || d._id) === developerId);
    return developer ? developer.name : developerId;
  };

  // Восстановление задачи из архива
  const handleRestoreTask = async (taskId) => {
    try {
      await axiosInstance.post(`/restoretask/${taskId}`);
      await fetchData();
    } catch (error) {
      console.error('Ошибка при восстановлении задачи:', error);
    }
  };

  // Полное удаление задачи из архива
  const handleDeleteTask = async (taskId) => {
    try {
      await axiosInstance.delete(`/deletetask/${taskId}`);
      await fetchData();
    } catch (error) {
      console.error('Ошибка при удалении задачи:', error);
    }
  };

  // Открытие модального окна подтверждения удаления
  const openDeleteModal = (taskId, taskTitle) => {
    setDeleteModal({ isOpen: true, taskId, taskTitle });
  };

  // Закрытие модального окна
  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, taskId: null, taskTitle: '' });
  };

  if (isLoading) {
    return <div className="p-8 text-center">Загрузка архива...</div>;
  }

  // Проверяем, есть ли задачи в архиве
  const hasTasks = Object.keys(groupedTasks).length > 0;

  if (!hasTasks) {
    return (
      <div className="p-8">
        <EmptyState
          icon={ArchiveBoxIcon}
          title="Архив задач пустой"
          description="В архиве пока нет завершенных задач. Завершенные задачи появятся здесь автоматически."
        />
      </div>
    );
  }

  return (
    <div className="p-8">
      {Object.entries(groupedTasks).map(([month, developers]) => (
        <div key={month} className="mb-8">
          <h2
            className="text-xl font-semibold mb-2 cursor-pointer"
            onClick={() =>
              setExpandedMonths((prev) => ({
                ...prev,
                [month]: !prev[month],
              }))
            }
          >
            {month} {expandedMonths[month] ? "▼" : "►"}
          </h2>
          {expandedMonths[month] &&
            Object.entries(developers).map(([developer, archivetasks]) => {
              const totalHours = archivetasks.reduce(
                (sum, task) => sum + (Number(task.hours) || 0),
                0
              );
              return (
                <div key={developer} className="ml-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium">
                      {getDeveloperInfo(developer)} - {totalHours} ч
                    </h3>
                  </div>
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="w-full border-collapse text-sm mb-4">
                      <thead>
                        <tr>
                          <th className="border p-2 w-1/6">Название</th>
                          <th className="border p-2 w-1/6">Описание</th>
                          <th className="border p-2 w-1/6">Проект</th>
                          <th className="border p-2 w-1/6">Часы</th>
                          <th className="border p-2 w-1/6">Создано</th>
                          <th className="border p-2 w-1/6">Завершено</th>
                          <th className="border p-2 w-1/6">Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {archivetasks.map((task) => (
                          <tr key={task.id || task._id}>
                            <TruncatedCell text={task.title} lines={2} />
                            <TruncatedCell text={task.description} lines={3} />
                            <TruncatedCell text={task.projectName} lines={2} />
                            <td className="border p-2">{task.hours}</td>
                            <td className="border p-2">
                              {task.createdAt ? formatDateTimeLocal(task.createdAt) : "—"}
                            </td>
                            <td className="border p-2">
                              {task.completedAt ? formatDateTimeLocal(task.completedAt) : "—"}
                            </td>
                            <td className="border p-2">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleRestoreTask(task.id || task._id)}
                                  className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition text-xs"
                                >
                                  Вернуть
                                </button>
                                <button
                                  onClick={() => openDeleteModal(task.id || task._id, task.title)}
                                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition text-xs"
                                >
                                  Удалить
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
        </div>
      ))}
      
      {/* Модальное окно подтверждения удаления */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={() => handleDeleteTask(deleteModal.taskId)}
        title="Удаление задачи"
        message={`Вы уверены, что хотите полностью удалить задачу "${deleteModal.taskTitle}"? Это действие нельзя отменить.`}
      />
    </div>
  );
};

export default ArchiveTable; 